import { useFetch } from "@hyper-fetch/react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { CreditCard } from "lucide-react";
import { LayoutGroup } from "motion/react";
import { z } from "zod";

import { sdk } from "@/api/sdk";
import { PageShell } from "@/components/layouts/shells/page-shell";
import { PLANS } from "@/features/billing/constants/plans";
import { BillingPreferences } from "@/features/billing/modules/billing-preferences/billing-preferences";
import { CheckoutSuccess } from "@/features/billing/modules/checkout-success/checkout-success";
import { ManageBillingButton } from "@/features/billing/modules/manage-billing/manage-billing-button";
import { PricingCard } from "@/features/billing/modules/pricing-card/pricing-card";
import { hasPermissionInList } from "@/features/navigation/filter-nav-entries";
import { m } from "@/paraglide/messages.js";
import { PermissionAction, PermissionSubject } from "@inginuiti/enums";

export const Route = createFileRoute("/(private)/billing/")({
  beforeLoad: async () => {
    const { data } = await sdk.users.me.$get.send();
    const permissions = data?.permissions ?? null;

    if (!hasPermissionInList(permissions, PermissionAction.MANAGE, PermissionSubject.BILLING)) {
      throw redirect({ to: "/" });
    }
  },
  component: RouteComponent,
  loader: () => {
    return {
      crumb: m.billing_nav_label(),
    };
  },
  validateSearch: z.object({
    checkout: z.literal("success").optional(),
  }),
});

function RouteComponent() {
  const { data } = useFetch(sdk.checkout.status.$get);
  const currentPlan = data?.plan ?? "free";

  return (
    <div className="w-full space-y-10">
      <CheckoutSuccess />
      <PageShell
        icon={CreditCard}
        title={m.billing_page_title()}
        description={m.billing_page_description()}
        actions={<ManageBillingButton />}
      >
        <LayoutGroup>
          <div data-tour="billing-plans" className="flex flex-wrap items-stretch justify-start gap-5">
            {PLANS.map((plan, index) => (
              <PricingCard
                key={plan.key}
                plan={plan.key}
                name={plan.name}
                tagline={plan.tagline}
                icon={plan.icon}
                features={plan.features}
                currentPlan={currentPlan}
                index={index}
              />
            ))}
          </div>
        </LayoutGroup>

        <BillingPreferences />
      </PageShell>
    </div>
  );
}
